import type { LucideIcon } from 'lucide-react';
import { ChevronDown, Copy } from 'lucide-react';
import toast from 'react-hot-toast';
import { buildOrgEmbedIframeSnippet } from '@/lib/orgEmbed';
import { Button, Field, FieldDescription, FieldLabel, Input, Textarea } from '@/components/ui';
import { cn } from '@/lib/utils';

export interface WebsiteEmbedWidget {
  id: string;
  title: string;
  description: string;
  embedUrl: string;
  iframeTitle: string;
  allowPayment?: boolean;
  icon?: LucideIcon;
}

function copyToClipboard(value: string, toastLabel: string) {
  navigator.clipboard?.writeText(value);
  toast.success(toastLabel);
}

function CopyableSnippetField({
  label,
  value,
  rows = 3,
  toastLabel,
  disabled = false,
  multiline = true,
}: {
  label: string;
  value: string;
  rows?: number;
  toastLabel: string;
  disabled?: boolean;
  multiline?: boolean;
}) {
  return (
    <Field>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <FieldLabel>{label}</FieldLabel>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={() => copyToClipboard(value, toastLabel)}
        >
          <Copy className="mr-1.5 h-3.5 w-3.5" aria-hidden />
          Copy
        </Button>
      </div>
      {multiline ? (
        <Textarea readOnly disabled={disabled} rows={rows} value={value} className="font-mono text-xs" />
      ) : (
        <Input readOnly disabled={disabled} value={value} className="font-mono text-xs" />
      )}
    </Field>
  );
}

export function WebsiteEmbedWidgetCard({
  widget,
  disabled = false,
}: {
  widget: WebsiteEmbedWidget;
  disabled?: boolean;
}) {
  const snippet = buildOrgEmbedIframeSnippet(widget.embedUrl, widget.iframeTitle, {
    allowPayment: widget.allowPayment,
  });
  const Icon = widget.icon;

  return (
    <article
      className={cn(
        'overflow-hidden rounded-xl border border-border bg-card shadow-sm',
        disabled && 'opacity-60'
      )}
    >
      <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
        <div className="flex min-w-0 items-start gap-3">
          {Icon ? (
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
              <Icon className="h-5 w-5 text-primary" aria-hidden />
            </div>
          ) : null}
          <div className="min-w-0">
            <h4 className="font-semibold text-foreground">{widget.title}</h4>
            <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{widget.description}</p>
          </div>
        </div>

        <div className="flex shrink-0 flex-wrap gap-2 sm:justify-end">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled}
            onClick={() => copyToClipboard(widget.embedUrl, `${widget.title} URL copied`)}
          >
            <Copy className="mr-1.5 h-3.5 w-3.5" aria-hidden />
            Copy URL
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={disabled}
            onClick={() => copyToClipboard(snippet, `${widget.title} snippet copied`)}
          >
            <Copy className="mr-1.5 h-3.5 w-3.5" aria-hidden />
            Copy iframe
          </Button>
        </div>
      </div>

      <details className="group border-t border-border bg-muted/20">
        <summary
          className="flex cursor-pointer list-none items-center justify-between gap-2 px-4 py-3 text-sm font-medium text-foreground marker:content-none hover:bg-muted/40 [&::-webkit-details-marker]:hidden"
        >
          <span>Preview embed code</span>
          <ChevronDown
            className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180"
            aria-hidden
          />
        </summary>
        <div className="space-y-4 border-t border-border bg-card px-4 py-4">
          <CopyableSnippetField
            label="Embed URL"
            value={widget.embedUrl}
            toastLabel={`${widget.title} URL copied`}
            disabled={disabled}
            multiline={false}
          />
          <CopyableSnippetField
            label="Iframe snippet"
            value={snippet}
            rows={3}
            toastLabel={`${widget.title} snippet copied`}
            disabled={disabled}
          />
        </div>
      </details>
    </article>
  );
}

export function WebsiteEmbedWidgetsGrid({
  widgets,
  disabled = false,
  emptyMessage = 'No embeddable widgets are available on your current subscription.',
}: {
  widgets: WebsiteEmbedWidget[];
  disabled?: boolean;
  emptyMessage?: string;
}) {
  if (widgets.length === 0) {
    return <p className="text-sm text-muted-foreground">{emptyMessage}</p>;
  }

  return (
    <div className="flex flex-col gap-3">
      {widgets.map((widget) => (
        <WebsiteEmbedWidgetCard key={widget.id} widget={widget} disabled={disabled} />
      ))}
    </div>
  );
}

export function WebsiteEmbedLinkOutFields({
  loginUrl,
  registerUrl,
  disabled = false,
}: {
  loginUrl: string;
  registerUrl: string;
  disabled?: boolean;
}) {
  return (
    <div className="space-y-4 rounded-xl border border-border bg-muted/10 p-4 sm:p-5">
      <div>
        <h4 className="text-sm font-semibold text-foreground">Sign-in link-outs</h4>
        <FieldDescription className="mt-1">
          Login and registration must open in a new tab — Firebase auth is not supported inside iframes.
        </FieldDescription>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <CopyableSnippetField
          label="Sign in"
          value={loginUrl}
          rows={2}
          toastLabel="Sign-in link copied"
          disabled={disabled}
        />
        <CopyableSnippetField
          label="Register"
          value={registerUrl}
          rows={2}
          toastLabel="Register link copied"
          disabled={disabled}
        />
      </div>
    </div>
  );
}
